// A dated record of the headline numbers, so someone who comes back a few
// months later can see how their plan has moved. Kept on the device, one entry
// per day per plan per owner, outside the synced plan document.

import { DEVICE_OWNER } from './ownerStorage';

const KEY = 'ultronic_progress_v2';
const LEGACY_KEY = 'ultronic_progress';
export const MAX_ENTRIES = 60;

export const dayOf = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Adds today's numbers, replacing an earlier entry from the same day. Oldest entries drop off. */
export function addSnapshot(list, snapshot) {
  const clean = (Array.isArray(list) ? list : []).filter((entry) => entry && entry.date && entry.date !== snapshot.date);
  return [...clean, snapshot].sort((a, b) => (a.date < b.date ? -1 : 1)).slice(-MAX_ENTRIES);
}

export function snapshotOf(result, saved, date = dayOf()) {
  const base = result.baseline;
  return {
    date,
    saved: Math.round(saved),
    spending: Math.round(result.spending),
    plannedAge: result.plannedAge,
    earliestAge: base.earliestAge,
    supported: Math.round(base.supported),
    successRate: Math.round(base.successRate * 1000) / 1000,
  };
}

/** The latest entry against the one before it; null until there are two days to compare. */
export function progressSince(list) {
  if (!Array.isArray(list) || list.length < 2) return null;
  const latest = list[list.length - 1];
  const previous = list[list.length - 2];
  return {
    latest,
    previous,
    first: list[0],
    saved: latest.saved - previous.saved,
    supported: latest.supported - previous.supported,
    // Positive means the earliest retirement age came down.
    yearsSooner: latest.earliestAge != null && previous.earliestAge != null ? previous.earliestAge - latest.earliestAge : null,
  };
}

// Entries are filed under whoever they belong to (an account's id, or the
// device when signed out) and then by plan. Every first plan has the same id,
// so filing by plan alone showed one person's numbers to the next person to
// use the browser. The earlier store was filed that way, so it cannot be
// attributed to anyone and is removed.
export { DEVICE_OWNER };

function readAll() {
  try {
    localStorage.removeItem(LEGACY_KEY);
    const stored = JSON.parse(localStorage.getItem(KEY) || '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  } catch {
    return {};
  }
}

const plansOf = (all, owner) => {
  const plans = all[owner];
  return plans && typeof plans === 'object' && !Array.isArray(plans) ? plans : {};
};

function writeAll(all) {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Storage full or unavailable: the history is a convenience, not the plan.
  }
}

export function readHistory(planId, owner = DEVICE_OWNER) {
  const list = plansOf(readAll(), owner)[planId];
  return Array.isArray(list) ? list : [];
}

export function recordSnapshot(planId, snapshot, owner = DEVICE_OWNER) {
  const all = readAll();
  const plans = plansOf(all, owner);
  const next = addSnapshot(plans[planId], snapshot);
  writeAll({ ...all, [owner]: { ...plans, [planId]: next } });
  return next;
}

export function clearHistory(planId, owner = DEVICE_OWNER) {
  const all = readAll();
  const plans = { ...plansOf(all, owner) };
  delete plans[planId];
  writeAll({ ...all, [owner]: plans });
  return [];
}

/** Removes everything recorded for one owner: used when an account signs out or is deleted. */
export function forgetOwnerHistory(owner) {
  const all = readAll();
  if (!(owner in all)) return;
  delete all[owner];
  writeAll(all);
}
